import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { CloudDownload, KeyRound, Loader2, PlugZap } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' }) : '–');

export const KboAutoImportPanel = ({ importing, onStarted }) => {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ username: '', password: '' });
  const [busy, setBusy] = useState('');

  const load = async () => {
    try { const { data } = await axios.get(`${API}/admin/kbo-open-data/config`, { withCredentials: true }); setCfg(data); setForm((f) => ({ ...f, username: data.username })); } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, []);

  const save = async (extra = {}) => {
    setBusy('save');
    try {
      const body = { username: form.username, ...(form.password ? { password: form.password } : {}), ...extra };
      const { data } = await axios.put(`${API}/admin/kbo-open-data/config`, body, { withCredentials: true });
      setCfg(data); setForm((f) => ({ ...f, password: '' }));
      toast.success('Paramètres BCE enregistrés');
      return true;
    } catch (e) { toast.error(e.response?.data?.detail || 'Enregistrement impossible'); return false; } finally { setBusy(''); }
  };

  const check = async () => {
    if (form.password || form.username !== cfg?.username) { if (!(await save())) return; }
    setBusy('check');
    try {
      const { data } = await axios.post(`${API}/admin/kbo-open-data/check`, {}, { withCredentials: true });
      toast.success(`Connexion OK — dernier fichier : ${data.latest.name}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Test impossible'); load(); } finally { setBusy(''); }
  };

  const fetchNow = async () => {
    if (form.password || form.username !== cfg?.username) { if (!(await save())) return; }
    if (!window.confirm('Télécharger le dernier fichier BCE Open Data (~300 Mo) sur le serveur et lancer l\'extraction ?')) return;
    setBusy('fetch');
    try {
      const { data } = await axios.post(`${API}/admin/kbo-open-data/fetch`, {}, { withCredentials: true });
      if (data.skipped) toast.info(data.reason);
      else toast.success(`Téléchargement de ${data.file} lancé — suivez l'état ci-dessus`);
      onStarted?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Lancement impossible'); } finally { setBusy(''); }
  };

  const toggleAuto = async (v) => { const { data } = await axios.put(`${API}/admin/kbo-open-data/config`, { auto_import: v }, { withCredentials: true }); setCfg(data); toast.success(v ? 'Import trimestriel activé' : 'Import trimestriel désactivé'); };

  if (!cfg) return null;
  return (
    <div className="rounded-lg border border-[#E5E7EB] bg-white p-3 text-sm space-y-3" data-testid="kbo-auto-import-panel">
      <p className="font-semibold text-[#111827] flex items-center gap-2"><CloudDownload className="w-4 h-4 text-[#1E3A8A]" /> Import automatique depuis le portail BCE Open Data</p>
      <p className="text-xs text-[#6B7280]">Le serveur se connecte avec votre compte BCE Open Data, repère le dernier fichier <strong>_Full.zip</strong> et lance l'extraction — plus besoin de téléverser 300 Mo depuis votre ordinateur. Vos identifiants sont stockés côté serveur, réservés à cet usage.</p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Input placeholder="Nom d'utilisateur BCE" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="input-field h-9" autoComplete="off" data-testid="kbo-config-username" />
        <Input type="password" placeholder={cfg.has_password ? 'Mot de passe enregistré (laisser vide pour conserver)' : 'Mot de passe BCE'} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input-field h-9" autoComplete="new-password" data-testid="kbo-config-password" />
        <Button size="sm" variant="outline" onClick={() => save()} disabled={!!busy || !form.username} data-testid="kbo-config-save-btn">{busy === 'save' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <KeyRound className="w-4 h-4 mr-1" />} Enregistrer</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={check} disabled={!!busy || !form.username || (!cfg.has_password && !form.password)} data-testid="kbo-config-check-btn">{busy === 'check' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlugZap className="w-4 h-4 mr-1" />} Tester la connexion</Button>
        <Button size="sm" onClick={fetchNow} disabled={!!busy || importing || !cfg.has_password && !form.password || !form.username} data-testid="kbo-fetch-now-btn">{busy === 'fetch' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1" />} Télécharger et importer maintenant</Button>
        <label className="flex items-center gap-2 text-xs text-[#374151] ml-auto"><Switch checked={cfg.auto_import} onCheckedChange={toggleAuto} data-testid="kbo-auto-import-switch" /> Import automatique chaque trimestre (1er janv./avr./juil./oct., 03h)</label>
      </div>
      <p className="text-[11px] text-[#6B7280]" data-testid="kbo-config-status">
        Dernier test : {fmtDate(cfg.last_checked_at)}{cfg.last_check_result ? ` — ${cfg.last_check_result}` : ''}
        {cfg.latest_available ? ` · dernier fichier disponible : ${cfg.latest_available.name}` : ''}
        {cfg.auto_import ? ` · prochain import planifié : ${fmtDate(cfg.next_auto_run)}` : ''}
        {cfg.last_auto_run ? ` · dernier import planifié : ${cfg.last_auto_run}` : ''}
      </p>
    </div>
  );
};
